<script>
  import { agentToken, agentWorkspace } from "./store/agent.js";
  import { onMount } from "svelte";

  // Auth state
  let email = "";
  let password = "";
  let loginError = null;
  let isLoggingIn = false;

  // Workspace state
  let workspaces = [];
  let loadingWorkspaces = false;
  let workspaceError = null;
  let manualWorkspaceId = "";

  // Chat state
  let messages = [];
  let currentQuery = "";
  let isSending = false;

  // Token override
  let overrideToken = "";

  // MCP Tools
  let mcpTools = null;
  let loadingTools = false;
  let toolsError = null;

  $: isLoggedIn = $agentToken !== "";

  const handleLogin = async () => {
    isLoggingIn = true;
    loginError = null;

    try {
      const response = await fetch(`${process.env.API_ROOT}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || data.code) {
        loginError = data.message || `HTTP ${response.status}`;
        return;
      }

      agentToken.set(data.token);
      email = "";
      password = "";
    } catch (e) {
      loginError = e.message || "Connection failed";
    } finally {
      isLoggingIn = false;
    }
  };

  const handleLogout = () => {
    agentToken.set("");
    agentWorkspace.set("");
    messages = [];
    workspaces = [];
  };

  const applyTokenOverride = () => {
    if (overrideToken.trim()) {
      agentToken.set(overrideToken.trim());
      overrideToken = "";
      workspaces = [];
      fetchWorkspaces();
    }
  };

  const fetchTools = async () => {
    loadingTools = true;
    toolsError = null;
    try {
      const response = await fetch(`${process.env.API_ROOT}/agent/tools`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      mcpTools = await response.json();
    } catch (e) {
      toolsError = e.message;
    } finally {
      loadingTools = false;
    }
  };

  const usePrompt = (prompt) => {
    currentQuery = prompt;
  };

  const clearChat = () => {
    messages = [];
  };

  // Contextual prompts based on workspace selection
  const generalPrompts = [
    "What workspaces do I have access to?",
    "Show me my profile",
    "Where am I logged in?",
  ];

  const workspacePrompts = [
    "How many songs do I have?",
    "List my playlists",
    "How much storage am I using?",
    "Who are the members of this workspace?",
    "What's my subscription plan?",
    "Show me my recent songs",
  ];

  $: contextualPrompts = $agentWorkspace ? workspacePrompts : generalPrompts;

  const fetchWorkspaces = async () => {
    loadingWorkspaces = true;
    workspaceError = null;

    try {
      const response = await fetch(`${process.env.API_ROOT}/workspace/v2/list`, {
        headers: {
          Authorization: `Bearer ${$agentToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      workspaces = data.records || data || [];
    } catch (e) {
      workspaceError = e.message;
    } finally {
      loadingWorkspaces = false;
    }
  };

  const selectWorkspace = (workspaceId) => {
    agentWorkspace.set(workspaceId);
    manualWorkspaceId = workspaceId;
  };

  const handleManualWorkspaceChange = () => {
    agentWorkspace.set(manualWorkspaceId);
  };

  const clearWorkspace = () => {
    agentWorkspace.set("");
    manualWorkspaceId = "";
  };

  const sendMessage = async () => {
    if (!currentQuery.trim() || isSending) return;

    const query = currentQuery.trim();
    currentQuery = "";
    isSending = true;

    messages = [...messages, { role: "user", content: query, timestamp: new Date() }];
    scrollToBottom();

    try {
      const headers = {
        Authorization: `Bearer ${$agentToken}`,
        "Content-Type": "application/json",
      };

      if ($agentWorkspace) {
        headers["control-workspace"] = $agentWorkspace;
      }

      // Build conversation history for multi-turn support
      const conversationHistory = messages
        .filter(m => m.role === "user" || m.role === "assistant")
        .map(m => {
          if (m.role === "user") {
            return { role: "user", content: m.content };
          }
          // For assistant messages, include tool results if present
          let content = m.message || "";
          if (m.toolCalls && m.toolCalls.length > 0) {
            const toolData = m.toolCalls.map(tc => ({
              tool: tc.name,
              result: tc.result
            }));
            content += "\n\n[Tool Results]\n" + JSON.stringify(toolData, null, 2);
          }
          return { role: "assistant", content };
        });

      const response = await fetch(`${process.env.API_ROOT}/agent/ask`, {
        method: "POST",
        headers,
        body: JSON.stringify({ messages: conversationHistory }),
      });

      const data = await response.json();

      if (!response.ok) {
        messages = [...messages, { 
          role: "error", 
          content: data.message || `HTTP ${response.status}`,
          timestamp: new Date()
        }];
      } else {
        messages = [...messages, { 
          role: "assistant", 
          message: data.message || data.response || data.answer || null,
          toolCalls: data.tool_calls || null,
          usage: data.usage || null,
          raw: data,
          timestamp: new Date(),
          showRaw: false
        }];
      }
    } catch (e) {
      messages = [...messages, { 
        role: "error", 
        content: e.message || "Request failed",
        timestamp: new Date()
      }];
    } finally {
      isSending = false;
      scrollToBottom();
    }
  };

  const handleKeydown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  let chatContainer;
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const toggleRaw = (index) => {
    messages = messages.map((m, i) => 
      i === index ? { ...m, showRaw: !m.showRaw } : m
    );
  };

  // Simple markdown-ish rendering
  const renderMessage = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');
  };

  onMount(() => {
    if (isLoggedIn) {
      fetchWorkspaces();
    }
  });

  $: if (isLoggedIn && workspaces.length === 0 && !loadingWorkspaces) {
    fetchWorkspaces();
  }
</script>

<div class="agent-container">
  <header class="agent-header">
    <div class="header-left">
      <h1>Agent Testing</h1>
      <span class="header-badge">DEV</span>
    </div>
    {#if isLoggedIn}
      <div class="header-right">
        {#if messages.length > 0}
          <button class="new-chat-btn" on:click={clearChat}>
            New Chat
          </button>
        {/if}
        <div class="workspace-indicator" class:active={$agentWorkspace}>
          {#if $agentWorkspace}
            <span class="ws-label">Workspace:</span>
            <code>{$agentWorkspace}</code>
            <button class="clear-btn" on:click={clearWorkspace} title="Clear workspace">x</button>
          {:else}
            <span class="ws-none">No workspace selected</span>
          {/if}
        </div>
        <button class="logout-btn" on:click={handleLogout}>Logout</button>
      </div>
    {/if}
  </header>

  {#if !isLoggedIn}
    <div class="login-section">
      <div class="login-box">
        <h2>Login</h2>
        <p class="login-hint">Uses /login endpoint (not admin)</p>
        
        {#if loginError}
          <div class="error-box">{loginError}</div>
        {/if}

        <div class="form-field">
          <label for="agent-email">Email</label>
          <input
            id="agent-email"
            type="email"
            bind:value={email}
            placeholder="user@example.com"
            disabled={isLoggingIn}
          />
        </div>

        <div class="form-field">
          <label for="agent-password">Password</label>
          <input
            id="agent-password"
            type="password"
            bind:value={password}
            placeholder="Password"
            disabled={isLoggingIn}
            on:keydown={(e) => e.key === "Enter" && handleLogin()}
          />
        </div>

        <button class="login-btn" on:click={handleLogin} disabled={isLoggingIn}>
          {isLoggingIn ? "Authenticating..." : "Login"}
        </button>
      </div>
    </div>
  {:else}
    <div class="main-layout">
      <aside class="sidebar">
        <div class="sidebar-section">
          <h3>JWT Token</h3>
          <div class="token-display">
            <code class="token-preview" title={$agentToken}>{$agentToken.slice(0, 20)}...{$agentToken.slice(-10)}</code>
            <button class="copy-btn" on:click={() => navigator.clipboard.writeText($agentToken)} title="Copy token">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
          <details class="token-override">
            <summary>Override token</summary>
            <textarea 
              class="token-input"
              placeholder="Paste JWT token here..."
              bind:value={overrideToken}
            ></textarea>
            <button 
              class="apply-token-btn" 
              on:click={applyTokenOverride}
              disabled={!overrideToken.trim()}
            >
              Apply Token
            </button>
          </details>
        </div>

        <div class="sidebar-section">
          <h3>Workspace</h3>
          <div class="workspace-input-group">
            <input
              type="text"
              bind:value={manualWorkspaceId}
              placeholder="Enter workspace ID"
              on:blur={handleManualWorkspaceChange}
              on:keydown={(e) => e.key === "Enter" && handleManualWorkspaceChange()}
            />
          </div>
        </div>

        <div class="sidebar-section">
          <div class="section-header">
            <h3>Available Workspaces</h3>
            <button class="refresh-btn" on:click={fetchWorkspaces} disabled={loadingWorkspaces}>
              {loadingWorkspaces ? "..." : "Refresh"}
            </button>
          </div>

          {#if workspaceError}
            <div class="error-small">{workspaceError}</div>
          {/if}

          <div class="workspace-list">
            {#if loadingWorkspaces}
              <div class="loading-item">Loading workspaces...</div>
            {:else if workspaces.length === 0}
              <div class="empty-item">No workspaces found</div>
            {:else}
              {#each workspaces as ws}
                <button
                  class="workspace-item"
                  class:selected={$agentWorkspace === ws.string_id}
                  on:click={() => selectWorkspace(ws.string_id)}
                >
                  <span class="ws-title">{ws.title || "Untitled"}</span>
                  <code class="ws-id">{ws.string_id}</code>
                </button>
              {/each}
            {/if}
          </div>
        </div>
      </aside>

      <main class="chat-area">
        <div class="chat-messages" bind:this={chatContainer}>
          {#if messages.length === 0}
            <div class="chat-empty">
              <div class="capabilities-header">
                <h2>Agent Capabilities</h2>
                {#if !mcpTools && !loadingTools}
                  <button class="load-tools-btn" on:click={fetchTools}>Load available tools</button>
                {/if}
              </div>

              {#if loadingTools}
                <div class="loading-tools">Loading capabilities...</div>
              {:else if toolsError}
                <div class="error-small">{toolsError}</div>
              {:else if mcpTools}
                <div class="tools-grid">
                  {#each mcpTools.categories as category}
                    <div class="tool-category">
                      <h3>{category.name}</h3>
                      <p class="category-desc">{category.description}</p>
                      <ul class="tool-list">
                        {#each category.tools as tool}
                          <li title={tool.description}>{tool.name}</li>
                        {/each}
                      </ul>
                    </div>
                  {/each}
                </div>

                {#if mcpTools.example_prompts && mcpTools.example_prompts.length > 0}
                  <div class="example-prompts">
                    <h3>Try asking</h3>
                    <div class="prompts-list">
                      {#each mcpTools.example_prompts as prompt}
                        <button class="prompt-chip" on:click={() => usePrompt(prompt)}>
                          {prompt}
                        </button>
                      {/each}
                    </div>
                  </div>
                {/if}
              {:else}
                <div class="empty-hint">
                  <p>POST /agent/ask</p>
                </div>
              {/if}
            </div>
          {:else}
            {#each messages as msg, idx}
              <div class="message" class:user={msg.role === "user"} class:assistant={msg.role === "assistant"} class:error={msg.role === "error"}>
                <div class="message-header">
                  <span class="message-role">{msg.role}</span>
                  <span class="message-time">{formatTime(msg.timestamp)}</span>
                </div>
                <div class="message-content">
                  {#if msg.role === "user"}
                    <pre>{msg.content}</pre>
                  {:else if msg.role === "error"}
                    <pre>{msg.content}</pre>
                  {:else if msg.role === "assistant"}
                    {#if msg.message}
                      <div class="rendered-message">{@html renderMessage(msg.message)}</div>
                    {:else}
                      <div class="no-message">No message in response</div>
                    {/if}
                    
                    {#if msg.toolCalls && msg.toolCalls.length > 0}
                      <div class="tool-calls-section">
                        <div class="tool-calls-header">
                          <span class="tool-icon">⚙</span>
                          <span>{msg.toolCalls.length} tool call{msg.toolCalls.length > 1 ? 's' : ''}</span>
                        </div>
                        <div class="tool-calls-list">
                          {#each msg.toolCalls as tc}
                            <div class="tool-call">
                              <div class="tool-name">{tc.name}</div>
                              {#if tc.arguments}
                                <div class="tool-args">
                                  <pre>{JSON.stringify(tc.arguments, null, 2)}</pre>
                                </div>
                              {/if}
                              {#if tc.result}
                                <details class="tool-result">
                                  <summary>Result ({Array.isArray(tc.result) ? tc.result.length + ' items' : 'object'})</summary>
                                  <pre>{JSON.stringify(tc.result, null, 2)}</pre>
                                </details>
                              {/if}
                            </div>
                          {/each}
                        </div>
                      </div>
                    {/if}

                    {#if msg.usage}
                      <div class="usage-info">
                        <span class="usage-model">{msg.usage.model || 'gpt-4o'}</span>
                        <span class="usage-tokens">{msg.usage.total_tokens} tokens</span>
                        <span class="usage-cost">${msg.usage.cost_usd?.toFixed(4) || '0.0000'}</span>
                      </div>
                    {/if}

                    <button class="toggle-raw" on:click={() => toggleRaw(idx)}>
                      {msg.showRaw ? 'Hide' : 'Show'} raw response
                    </button>
                    {#if msg.showRaw}
                      <div class="raw-response">
                        <pre>{JSON.stringify(msg.raw, null, 2)}</pre>
                      </div>
                    {/if}
                  {/if}
                </div>
              </div>
            {/each}
          {/if}
          {#if isSending}
            <div class="message assistant sending">
              <div class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          {/if}
        </div>

        <div class="chat-input-area">
          <div class="input-row">
            <textarea
              bind:value={currentQuery}
              placeholder="Enter your query..."
              on:keydown={handleKeydown}
              disabled={isSending}
              rows="2"
            ></textarea>
            <button class="send-btn" on:click={sendMessage} disabled={isSending || !currentQuery.trim()}>
              {isSending ? "Sending..." : "Send"}
            </button>
          </div>
          <div class="quick-prompts">
            <span class="prompts-label">{$agentWorkspace ? 'Try:' : 'No workspace selected. Try:'}</span>
            {#each contextualPrompts as prompt}
              <button class="quick-prompt" on:click={() => usePrompt(prompt)}>{prompt}</button>
            {/each}
          </div>
        </div>
      </main>
    </div>
  {/if}
</div>

<style>
  .agent-container {
    min-height: 100vh;
    background: #0d1117;
    color: #c9d1d9;
    font-family: "SF Mono", "Fira Code", "Consolas", monospace;
    margin: -8px;
    width: calc(100% + 16px);
  }

  .agent-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    background: #161b22;
    border-bottom: 1px solid #30363d;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .agent-header h1 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #f0f6fc;
  }

  .header-badge {
    padding: 0.125rem 0.5rem;
    background: #238636;
    color: #fff;
    font-size: 0.625rem;
    font-weight: 700;
    border-radius: 3px;
    letter-spacing: 0.05em;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .workspace-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: #21262d;
    border: 1px solid #30363d;
    border-radius: 6px;
    font-size: 0.8125rem;
  }

  .workspace-indicator.active {
    border-color: #238636;
    background: rgba(35, 134, 54, 0.15);
  }

  .ws-label {
    color: #8b949e;
  }

  .workspace-indicator code {
    color: #58a6ff;
    font-size: 0.75rem;
  }

  .ws-none {
    color: #6e7681;
    font-style: italic;
  }

  .clear-btn {
    background: transparent;
    border: none;
    color: #f85149;
    cursor: pointer;
    padding: 0 0.25rem;
    font-size: 0.875rem;
    line-height: 1;
  }

  .new-chat-btn {
    padding: 0.375rem 0.875rem;
    background: #21262d;
    border: 1px solid #58a6ff;
    color: #58a6ff;
    border-radius: 6px;
    font-size: 0.8125rem;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: inherit;
  }

  .new-chat-btn:hover {
    background: #58a6ff;
    color: #fff;
  }

  .logout-btn {
    padding: 0.375rem 0.875rem;
    background: #21262d;
    border: 1px solid #f85149;
    color: #f85149;
    border-radius: 6px;
    font-size: 0.8125rem;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .logout-btn:hover {
    background: #f85149;
    color: #fff;
  }

  /* Login Section */
  .login-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 60px);
    padding: 2rem;
  }

  .login-box {
    width: 100%;
    max-width: 380px;
    background: #161b22;
    border: 1px solid #30363d;
    border-radius: 8px;
    padding: 2rem;
  }

  .login-box h2 {
    margin: 0 0 0.25rem;
    font-size: 1.5rem;
    color: #f0f6fc;
  }

  .login-hint {
    margin: 0 0 1.5rem;
    color: #6e7681;
    font-size: 0.8125rem;
  }

  .error-box {
    padding: 0.75rem 1rem;
    background: rgba(248, 81, 73, 0.1);
    border: 1px solid #f85149;
    border-radius: 6px;
    color: #f85149;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .form-field {
    margin-bottom: 1rem;
  }

  .form-field label {
    display: block;
    margin-bottom: 0.375rem;
    color: #8b949e;
    font-size: 0.8125rem;
  }

  .form-field input {
    width: 100%;
    padding: 0.625rem 0.75rem;
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 6px;
    color: #c9d1d9;
    font-size: 0.9375rem;
    font-family: inherit;
  }

  .form-field input:focus {
    outline: none;
    border-color: #58a6ff;
    box-shadow: 0 0 0 3px rgba(88, 166, 255, 0.15);
  }

  .login-btn {
    width: 100%;
    padding: 0.75rem;
    background: #238636;
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
    margin-top: 0.5rem;
  }

  .login-btn:hover:not(:disabled) {
    background: #2ea043;
  }

  .login-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Main Layout */
  .main-layout {
    display: flex;
    height: calc(100vh - 60px);
  }

  .sidebar {
    width: 300px;
    background: #161b22;
    border-right: 1px solid #30363d;
    overflow-y: auto;
    flex-shrink: 0;
  }

  .sidebar-section {
    padding: 1rem;
    border-bottom: 1px solid #30363d;
  }

  .sidebar-section h3 {
    margin: 0 0 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #8b949e;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .section-header h3 {
    margin: 0;
  }

  .workspace-input-group input {
    width: 100%;
    padding: 0.5rem 0.625rem;
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 6px;
    color: #c9d1d9;
    font-size: 0.8125rem;
    font-family: inherit;
  }

  .workspace-input-group input:focus {
    outline: none;
    border-color: #58a6ff;
  }

  /* Token Display */
  .token-display {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 6px;
    padding: 0.5rem;
  }

  .token-preview {
    flex: 1;
    font-size: 0.6875rem;
    color: #7ee787;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy-btn {
    background: transparent;
    border: none;
    color: #8b949e;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  .copy-btn:hover {
    background: #21262d;
    color: #c9d1d9;
  }

  .token-override {
    margin-top: 0.5rem;
  }

  .token-override summary {
    font-size: 0.6875rem;
    color: #8b949e;
    cursor: pointer;
    padding: 0.25rem 0;
  }

  .token-override summary:hover {
    color: #c9d1d9;
  }

  .token-input {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem;
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 6px;
    color: #c9d1d9;
    font-size: 0.6875rem;
    font-family: inherit;
    resize: vertical;
    min-height: 60px;
  }

  .token-input:focus {
    outline: none;
    border-color: #58a6ff;
  }

  .apply-token-btn {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem;
    background: #238636;
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s ease;
  }

  .apply-token-btn:hover:not(:disabled) {
    background: #2ea043;
  }

  .apply-token-btn:disabled {
    background: #21262d;
    color: #6e7681;
    cursor: not-allowed;
  }

  .refresh-btn {
    padding: 0.25rem 0.5rem;
    background: #21262d;
    border: 1px solid #30363d;
    border-radius: 4px;
    color: #8b949e;
    font-size: 0.6875rem;
    cursor: pointer;
    font-family: inherit;
  }

  .refresh-btn:hover:not(:disabled) {
    background: #30363d;
    color: #c9d1d9;
  }

  .error-small {
    padding: 0.5rem;
    background: rgba(248, 81, 73, 0.1);
    border-radius: 4px;
    color: #f85149;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .workspace-list {
    max-height: 400px;
    overflow-y: auto;
  }

  .loading-item, .empty-item {
    padding: 0.75rem;
    color: #6e7681;
    font-size: 0.8125rem;
    text-align: center;
  }

  .workspace-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 0.625rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    margin-bottom: 0.25rem;
    transition: all 0.15s ease;
  }

  .workspace-item:hover {
    background: #21262d;
    border-color: #30363d;
  }

  .workspace-item.selected {
    background: rgba(88, 166, 255, 0.1);
    border-color: #58a6ff;
  }

  .ws-title {
    display: block;
    color: #c9d1d9;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.125rem;
  }

  .ws-id {
    display: block;
    color: #6e7681;
    font-size: 0.6875rem;
  }

  /* Chat Area */
  .chat-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #0d1117;
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .chat-empty {
    padding: 2rem;
    max-width: 900px;
    margin: 0 auto;
  }

  .empty-hint {
    font-size: 0.75rem;
    color: #484f58;
  }

  /* Capabilities Display */
  .capabilities-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #30363d;
  }

  .capabilities-header h2 {
    margin: 0;
    font-size: 1.125rem;
    color: #f0f6fc;
  }

  .load-tools-btn {
    padding: 0.5rem 1rem;
    background: #21262d;
    border: 1px solid #30363d;
    border-radius: 6px;
    color: #58a6ff;
    font-size: 0.75rem;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s ease;
  }

  .load-tools-btn:hover {
    background: #30363d;
    border-color: #58a6ff;
  }

  .loading-tools {
    text-align: center;
    color: #8b949e;
    padding: 2rem;
  }

  .tools-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .tool-category {
    background: #161b22;
    border: 1px solid #30363d;
    border-radius: 8px;
    padding: 1rem;
  }

  .tool-category h3 {
    margin: 0 0 0.25rem;
    font-size: 0.875rem;
    color: #f0f6fc;
  }

  .category-desc {
    margin: 0 0 0.75rem;
    font-size: 0.6875rem;
    color: #8b949e;
  }

  .tool-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .tool-list li {
    padding: 0.25rem 0;
    font-size: 0.75rem;
    color: #7ee787;
    cursor: default;
  }

  .tool-list li::before {
    content: "→ ";
    color: #484f58;
  }

  .example-prompts {
    border-top: 1px solid #30363d;
    padding-top: 1.5rem;
  }

  .example-prompts h3 {
    margin: 0 0 1rem;
    font-size: 0.875rem;
    color: #8b949e;
    font-weight: 500;
  }

  .prompts-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .prompt-chip {
    padding: 0.5rem 0.875rem;
    background: #21262d;
    border: 1px solid #30363d;
    border-radius: 20px;
    color: #c9d1d9;
    font-size: 0.75rem;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s ease;
  }

  .prompt-chip:hover {
    background: #30363d;
    border-color: #58a6ff;
    color: #58a6ff;
  }

  .message {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 1px solid #30363d;
  }

  .message.user {
    background: #21262d;
    margin-left: 2rem;
  }

  .message.assistant {
    background: #161b22;
    margin-right: 2rem;
    border-color: #238636;
  }

  .message.error {
    background: rgba(248, 81, 73, 0.1);
    border-color: #f85149;
    margin-right: 2rem;
  }

  .message-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.6875rem;
  }

  .message-role {
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  .message.user .message-role { color: #58a6ff; }
  .message.assistant .message-role { color: #238636; }
  .message.error .message-role { color: #f85149; }

  .message-time {
    color: #484f58;
  }

  .message-content pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: inherit;
    font-size: 0.875rem;
    color: #c9d1d9;
  }

  .sending {
    background: #161b22;
    border-color: #30363d;
  }

  .typing-indicator {
    display: flex;
    gap: 4px;
    padding: 0.25rem 0;
  }

  .typing-indicator span {
    width: 6px;
    height: 6px;
    background: #6e7681;
    border-radius: 50%;
    animation: typing 1.2s infinite;
  }

  .typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
  .typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

  @keyframes typing {
    0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-3px); }
  }

  /* Chat Input */
  .chat-input-area {
    padding: 1rem;
    background: #161b22;
    border-top: 1px solid #30363d;
  }

  .input-row {
    display: flex;
    gap: 0.75rem;
  }

  .chat-input-area textarea {
    flex: 1;
    padding: 0.75rem;
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 8px;
    color: #c9d1d9;
    font-size: 0.9375rem;
    font-family: inherit;
    resize: none;
  }

  .chat-input-area textarea:focus {
    outline: none;
    border-color: #58a6ff;
  }

  .chat-input-area textarea::placeholder {
    color: #6e7681;
  }

  .send-btn {
    padding: 0.75rem 1.5rem;
    background: #238636;
    border: none;
    border-radius: 8px;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
    align-self: flex-end;
  }

  .send-btn:hover:not(:disabled) {
    background: #2ea043;
  }

  .send-btn:disabled {
    background: #21262d;
    color: #6e7681;
    cursor: not-allowed;
  }

  .quick-prompts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #30363d;
  }

  .prompts-label {
    font-size: 0.6875rem;
    color: #6e7681;
    margin-right: 0.25rem;
  }

  .quick-prompt {
    padding: 0.25rem 0.625rem;
    background: #21262d;
    border: 1px solid #30363d;
    border-radius: 12px;
    color: #8b949e;
    font-size: 0.6875rem;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s ease;
    white-space: nowrap;
  }

  .quick-prompt:hover {
    background: #30363d;
    color: #c9d1d9;
    border-color: #484f58;
  }

  /* Rendered Message */
  .rendered-message {
    font-size: 0.9375rem;
    line-height: 1.6;
    color: #e6edf3;
  }

  .rendered-message :global(strong) {
    color: #f0f6fc;
    font-weight: 600;
  }

  .rendered-message :global(em) {
    color: #8b949e;
    font-style: italic;
  }

  .rendered-message :global(code) {
    background: #21262d;
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    font-size: 0.8125rem;
    color: #79c0ff;
  }

  .no-message {
    color: #6e7681;
    font-style: italic;
    font-size: 0.8125rem;
  }

  /* Tool Calls */
  .tool-calls-section {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #30363d;
  }

  .tool-calls-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: #8b949e;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.75rem;
  }

  .tool-icon {
    font-size: 0.875rem;
  }

  .tool-calls-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .tool-call {
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 6px;
    padding: 0.75rem;
  }

  .tool-name {
    font-weight: 600;
    color: #d2a8ff;
    font-size: 0.8125rem;
    margin-bottom: 0.5rem;
  }

  .tool-args pre,
  .tool-result pre {
    margin: 0;
    font-size: 0.75rem;
    color: #8b949e;
    background: transparent;
    max-height: 150px;
    overflow: auto;
  }

  .tool-result {
    margin-top: 0.5rem;
  }

  .tool-result summary {
    cursor: pointer;
    font-size: 0.75rem;
    color: #58a6ff;
    padding: 0.25rem 0;
  }

  .tool-result summary:hover {
    color: #79c0ff;
  }

  .tool-result[open] summary {
    margin-bottom: 0.5rem;
  }

  /* Usage Info */
  .usage-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
    padding: 0.5rem 0.75rem;
    background: #0d1117;
    border-radius: 6px;
    font-size: 0.6875rem;
  }

  .usage-model {
    color: #d2a8ff;
    font-weight: 500;
  }

  .usage-tokens {
    color: #8b949e;
  }

  .usage-cost {
    color: #7ee787;
    font-weight: 500;
  }

  /* Raw Response Toggle */
  .toggle-raw {
    margin-top: 1rem;
    padding: 0.375rem 0.75rem;
    background: transparent;
    border: 1px solid #30363d;
    border-radius: 4px;
    color: #6e7681;
    font-size: 0.6875rem;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s ease;
  }

  .toggle-raw:hover {
    background: #21262d;
    color: #8b949e;
  }

  .raw-response {
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: #0d1117;
    border: 1px solid #30363d;
    border-radius: 6px;
    max-height: 300px;
    overflow: auto;
  }

  .raw-response pre {
    margin: 0;
    font-size: 0.6875rem;
    color: #6e7681;
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #0d1117;
  }

  ::-webkit-scrollbar-thumb {
    background: #30363d;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #484f58;
  }
</style>
